// src/api/paymentService.js
import { supabase } from '../../shared/supabase';
import { createNotification, createStaffNotifications } from '../../shared/api/notificationService';

const INDIAN_MOBILE_REGEX = /^[6-9]\d{9}$/;

/** Returns a valid 10-digit Indian mobile or '' when missing/invalid. */
export function normalizeIndianPhone(phone) {
  const digits = String(phone ?? '').replace(/\D/g, '');
  return INDIAN_MOBILE_REGEX.test(digits) ? digits : '';
}

/** Auto guest inbox when none provided — guest{timestamp}@tabletap.in */
export function generateGuestEmail(phoneOrSeed) {
  const digits = phoneOrSeed ? String(phoneOrSeed).replace(/\D/g, '') : '';
  const stamp = digits || `${Date.now()}${Math.floor(Math.random() * 1e6)}`;
  return `guest${stamp}@tabletap.in`;
}

/** True for synthetic guest emails that should not receive real billing mail. */
export function isGuestPlaceholderEmail(email) {
  if (!email || !String(email).trim()) return true;
  const normalized = String(email).trim().toLowerCase();
  if (normalized === 'guest@example.com') return true;
  return (
    normalized.startsWith('guest') &&
    (normalized.endsWith('@tabletap.in') || normalized.endsWith('@tabletap.com'))
  );
}

function resolveCustomerEmail(rawEmail, phone) {
  const trimmed = (rawEmail || '').trim();
  if (trimmed && !isGuestPlaceholderEmail(trimmed)) return trimmed;
  return generateGuestEmail(phone);
}

class PaymentService {
  /**
   * Load Razorpay script dynamically
   */
  loadRazorpayScript() {
    return new Promise((resolve, reject) => {
      if (window.Razorpay) {
        resolve(window.Razorpay);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.crossOrigin = 'anonymous';

      const timeout = setTimeout(() => {
        reject(new Error('Razorpay script loading timeout'));
      }, 10000);

      script.onload = () => {
        clearTimeout(timeout);
        if (!window.Razorpay) {
          reject(new Error('Razorpay not found in window object after script load'));
          return;
        }
        resolve(window.Razorpay);
      };

      script.onerror = (error) => {
        clearTimeout(timeout);
        reject(new Error('Failed to load Razorpay script'));
      };

      document.body.appendChild(script);
    });
  }

  /** Same image priority as restaurant selection: photo first, then logo. */
  getRestaurantPaymentImage(restaurant) {
    if (!restaurant) return null;

    const candidates = [restaurant.restaurant_photo_url, restaurant.logo_url];
    for (const url of candidates) {
      const trimmed = url?.trim();
      if (trimmed && /^https?:\/\//i.test(trimmed)) {
        return trimmed;
      }
    }
    return null;
  }

  /**
   * Initialize Razorpay payment
   */
  initializeRazorpayPayment({
    razorpayKeyId,
    amount,
    orderId,
    restaurantName,
    restaurantLogo,
    restaurantId,
    customerName,
    customerEmail,
    customerPhone,
    tableNumber,
    specialInstructions,
    themeColor,
    onSuccess,
    onDismiss,
    onFailure,
  }) {
    try {
      const paymentImage =
        restaurantLogo?.trim() && /^https?:\/\//i.test(restaurantLogo.trim())
          ? restaurantLogo.trim()
          : null;

      const rzp = new window.Razorpay({
        key: razorpayKeyId,
        amount: amount,
        currency: 'INR',
        // Bind the checkout to the server-created order so Razorpay enforces the
        // authoritative amount and returns a verifiable order_id + signature.
        ...(orderId ? { order_id: orderId } : {}),
        name: restaurantName || 'Restaurant Order',
        description: `Order at ${restaurantName || 'Restaurant'}`,
        ...(paymentImage ? { image: paymentImage } : {}),
        handler: onSuccess,
        prefill: {
          name: customerName || 'Valued Customer',
          email: customerEmail,
          contact: customerPhone,
        },
        theme: { color: themeColor || '#F37254' },
        modal: {
          ondismiss: onDismiss,
        },
        notes: {
          restaurant_id: restaurantId || '1',
          table_number: tableNumber || 'N/A',
          special_instructions: specialInstructions,
        },
      });

      rzp.on('payment.failed', (response) => {
        if (onFailure) {
          onFailure(response.error.description || 'Gateway error');
        }
      });

      return rzp;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create a server-authoritative Razorpay order. The server re-prices the
   * items from the database and returns the order id + amount to charge, so the
   * client can never dictate the payable amount.
   * @returns {Promise<{order_id:string, amount:number, currency:string, key_id:string, computed_total:number}>}
   */
  async _parseEdgeFunctionError(error, fallback = 'Request failed') {
    let message = error?.message || fallback;
    try {
      const parsed = await error?.context?.json?.();
      if (parsed?.error) message = parsed.error;
    } catch {
      /* response body wasn't JSON — keep the generic message */
    }
    return message;
  }

  async createRazorpayOrder({ restaurantId, items, tableNumber, liveOrderId }) {
    const body = {
      restaurant_id: restaurantId,
      table_number: tableNumber,
    };

    if (liveOrderId) {
      body.live_order_id = liveOrderId;
    } else {
      body.items = (items || []).map((it) => ({
        id: it.food_item_id || it.id,
        quantity: it.quantity,
      }));
    }

    const { data, error } = await supabase.functions.invoke('create-order', { body });

    if (error) {
      const message = await this._parseEdgeFunctionError(error, 'Failed to create payment order');
      throw new Error(message);
    }
    if (data?.error || !data?.order_id) {
      throw new Error(data?.error || 'Failed to create payment order');
    }
    return data;
  }

  /**
   * Verify Razorpay payment signature
   */
  async verifyRazorpaySignature(paymentId, orderId, signature) {
    try {
      const { data, error } = await supabase.functions.invoke('verify-payment', {
        body: {
          payment_id: paymentId,
          order_id: orderId,
          signature: signature,
        },
      });

      if (error) {
        console.error('Payment verification error:', error);
        return false;
      }

      return data?.verified === true;
    } catch (err) {
      console.error('Payment verification exception:', err);
      return false;
    }
  }

  /**
   * Calculate order totals with GST and discounts
   */
  calculateOrderTotals(items, discountValue = 0, discountType = 'percentage') {
    let originalTotal = 0;
    let discountAmount = 0;
    let taxableAmount = 0;

    items.forEach((item) => {
      const originalItemTotal = item.price * item.quantity;
      originalTotal += originalItemTotal;

      const effectivePrice = item.effectivePrice !== undefined ? item.effectivePrice : item.price;

      const itemDiscount = (item.price - effectivePrice) * item.quantity;
      discountAmount += itemDiscount;

      const itemSubtotal = effectivePrice * item.quantity;

      const isWater =
        item.name &&
        (item.name.toLowerCase().includes('water') || item.category?.toLowerCase() === 'water');

      if (!isWater) {
        taxableAmount += itemSubtotal;
      }
    });

    if (discountValue > 0) {
      let globalDiscountAmount = 0;
      if (discountType === 'percentage') {
        globalDiscountAmount = (originalTotal * discountValue) / 100;
        const discountRatio = discountValue / 100;
        taxableAmount = taxableAmount * (1 - discountRatio);
      } else if (discountType === 'flat') {
        globalDiscountAmount = Math.min(discountValue, originalTotal);
        const discountRatio = globalDiscountAmount / originalTotal;
        taxableAmount = taxableAmount * (1 - discountRatio);
      }
      discountAmount += globalDiscountAmount;
    }

    const cgst = taxableAmount * 0.025;
    const sgst = taxableAmount * 0.025;
    const subtotalAfterDiscount = originalTotal - discountAmount;
    const finalTotal = subtotalAfterDiscount + cgst + sgst;

    return {
      subtotal: originalTotal,
      discountAmount,
      subtotalAfterDiscount,
      cgst,
      sgst,
      finalTotal,
    };
  }

  /**
   * Create manager notification for new order
   * @private
   */
  async _createManagerOrderNotification(order) {
    try {
      await createStaffNotifications(
        {
          title: 'New Order Received',
          message: `Order #${order.order_number} received for ₹${order.total}`,
          type: 'order_received',
          order_id: order.id,
          metadata: {
            order_id: order.id,
            order_number: order.order_number,
            table_number: order.table_number,
            total_amount: order.total,
          },
        },
        order.restaurant_id,
      );
    } catch (error) {
      console.error('Failed to create manager notification:', error);
    }
  }

  /**
   * Create a new order in Supabase
   */
  async createOrder(orderData) {
    try {
      const rawPhone = (orderData.customerPhone || '').trim();
      const customerPhone = normalizeIndianPhone(rawPhone);
      const isLiveTableOrder = orderData.orderKind === 'live_order';

      if (!orderData.userId && !orderData.isAdmin) {
        if (!orderData.customerName || orderData.customerName.trim() === '') {
          throw new Error('Customer name is required.');
        }
        if (!isLiveTableOrder && rawPhone && !customerPhone) {
          throw new Error('Please enter a valid 10-digit phone number or leave it blank.');
        }
      }

      const totals = this.calculateOrderTotals(orderData.items, orderData.discountPercentage || 0);

      const order = {
        restaurant_id: orderData.restaurantId,
        user_id: orderData.userId || null,
        customer_name: (orderData.customerName || 'Walk-in Customer').trim(),
        customer_email: resolveCustomerEmail(orderData.customerEmail, customerPhone),
        customer_phone: customerPhone,
        table_number: orderData.tableNumber || 'Not Specified',
        items: orderData.items.map((item) => ({
          food_item_id: item.food_item_id || item.id,
          quantity: item.quantity,
          price: item.price,
          name: item.name,
          category: item.category,
          image: item.image,
          note: item.note || null,
        })),
        order_kind: orderData.orderKind || 'order',
        kot_count: orderData.kotCount ?? 0,
        subtotal: totals.subtotalAfterDiscount,
        gst: totals.cgst + totals.sgst,
        discount: totals.discountAmount,
        total: totals.finalTotal,
        razorpay_order_id: orderData.razorpayOrderId || null,
        razorpay_payment_id: orderData.razorpayPaymentId || null,
        razorpay_signature: orderData.razorpaySignature || null,
        payment_method: orderData.paymentMethod || 'razorpay',
        payment_status: orderData.paymentStatus || 'pending',
        payment_timestamp: orderData.paymentTimestamp || new Date().toISOString(),
        status: orderData.status || 'pending',
        special_instructions: orderData.specialInstructions || null,
        tracking: orderData.tracking || {
          confirmed: { status: true, timestamp: new Date().toISOString() },
          preparing: { status: false, timestamp: null },
          ready: { status: false, timestamp: null },
          completed: { status: false, timestamp: null },
        },
        // Let PostgreSQL assign these timestamps. Using the device clock here
        // can create orders dated in the future when a restaurant tablet has
        // an incorrect timezone or system time.
      };

      const { data, error } = await supabase.from('orders').insert([order]).select().single();

      if (error) {
        console.error('Supabase order creation error:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
        });
        throw error;
      }

      if (data.order_kind !== 'live_order') {
        await this._createManagerOrderNotification(data);
      }

      return data;
    } catch (error) {
      console.error('Error in createOrder function:', error.message);
      throw error;
    }
  }

  /**
   * Update payment status
   */
  async updatePaymentStatus(orderId, paymentData) {
    try {
      const updateData = {
        payment_status: paymentData.status,
        payment_timestamp: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      if (paymentData.razorpay_order_id) {
        updateData.razorpay_order_id = paymentData.razorpay_order_id;
      }
      if (paymentData.razorpay_payment_id) {
        updateData.razorpay_payment_id = paymentData.razorpay_payment_id;
      }
      if (paymentData.razorpay_signature) {
        updateData.razorpay_signature = paymentData.razorpay_signature;
      }
      if (paymentData.notes) {
        updateData.payment_notes = paymentData.notes;
      }

      const { data, error } = await supabase
        .from('orders')
        .update(updateData)
        .eq('id', orderId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Pay and close an active live order (open tab).
   */
  async completeLiveOrderPayment({
    orderId,
    paymentMethod,
    customerName,
    customerEmail,
    customerPhone,
    razorpayOrderId = null,
    razorpayPaymentId = null,
    razorpaySignature = null,
  }) {
    const { data, error } = await supabase.rpc('complete_live_order_payment', {
      p_order_id: orderId,
      p_payment_method: paymentMethod,
      p_customer_name: customerName || null,
      p_customer_email: customerEmail || null,
      p_customer_phone: customerPhone || null,
      p_razorpay_order_id: razorpayOrderId,
      p_razorpay_payment_id: razorpayPaymentId,
      p_razorpay_signature: razorpaySignature,
    });

    if (error) {
      console.error('completeLiveOrderPayment error:', error);
      throw new Error(error.message || 'Failed to complete live order payment');
    }

    return data;
  }

  /**
   * Get order by ID
   */
  async getOrderById(orderId) {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(
          `
          *,
          restaurant:restaurants(
            id,
            name,
            logo_url,
            contact_phone
          )
        `,
        )
        .eq('id', orderId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user orders
   */
  async getUserOrders(userId) {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(
          `
          *,
          restaurant:restaurants(
            id,
            name,
            logo_url
          )
        `,
        )
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get restaurant orders
   */
  async getRestaurantOrders(restaurantId, filters = {}) {
    try {
      let query = supabase
        .from('orders')
        .select(
          `
          *,
          user:users(
            id,
            email,
            user_metadata
          )
        `,
        )
        .eq('restaurant_id', restaurantId)
        .order('created_at', { ascending: false });

      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      if (filters.payment_status) {
        query = query.eq('payment_status', filters.payment_status);
      }
      if (filters.date_from) {
        query = query.gte('created_at', filters.date_from);
      }
      if (filters.date_to) {
        query = query.lte('created_at', filters.date_to);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Process payment with Razorpay
   */
  async processRazorpayPayment(paymentData) {
    try {
      const rzp = await this.initializeRazorpayPayment(paymentData);
      rzp.open();
      return rzp;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Process direct payment (pay at restaurant)
   */
  async processDirectPayment(orderData) {
    try {
      const result = await this.createOrder({
        ...orderData,
        razorpay_payment_id: 'DIRECT-' + Date.now(),
        razorpay_order_id: 'DIRECT-ORDER-' + Date.now(),
        razorpay_signature: 'NA',
        payment_method: 'direct',
        // Offline payment collected by the restaurant — created as pending and
        // confirmed by staff once collected. The DB enforces this too.
        payment_status: 'pending',
      });

      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Complete payment process
   */
  async completePayment(paymentResponse, orderData) {
    try {
      if (
        paymentResponse.razorpay_payment_id &&
        !paymentResponse.razorpay_payment_id.startsWith('DIRECT-') &&
        !paymentResponse.razorpay_payment_id.startsWith('UPI-')
      ) {
        const isValid = await this.verifyRazorpaySignature(
          paymentResponse.razorpay_payment_id,
          paymentResponse.razorpay_order_id,
          paymentResponse.razorpay_signature,
        );

        if (!isValid) {
          throw new Error('Payment verification failed');
        }
      }

      const isOfflinePayment =
        paymentResponse.razorpay_payment_id.startsWith('DIRECT-') ||
        paymentResponse.razorpay_payment_id.startsWith('UPI-');

      const order = await this.createOrder({
        ...orderData,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_signature: paymentResponse.razorpay_signature,
        // Offline payments stay pending until staff confirm collection.
        payment_status: isOfflinePayment ? 'pending' : 'completed',
      });

      await createNotification({
        title: 'Payment Successful',
        message: `Payment completed for order #${order.order_number}`,
        type: 'payment_success',
        user_id: orderData.userId,
        restaurant_id: orderData.restaurantId,
        metadata: {
          order_id: order.id,
          payment_id: paymentResponse.razorpay_payment_id,
          amount: orderData.total,
        },
      });

      return order;
    } catch (error) {
      await createNotification({
        title: 'Payment Failed',
        message: `Payment failed: ${error.message}`,
        type: 'payment_failed',
        user_id: orderData.userId,
        restaurant_id: orderData.restaurantId,
        metadata: {
          error: error.message,
          amount: orderData.total,
        },
      });

      throw error;
    }
  }

  /**
   * Format currency for display
   */
  formatCurrency(amount, currency = 'INR') {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  }

  /**
   * Validate payment data
   */
  validatePaymentData(paymentData) {
    const errors = [];

    if (!paymentData.amount || paymentData.amount <= 0) {
      errors.push('Invalid amount');
    }

    if (!paymentData.restaurantId) {
      errors.push('Restaurant ID is required');
    }

    if (!paymentData.customerName || paymentData.customerName.trim() === '') {
      errors.push('Customer name is required');
    }

    if (!paymentData.customerEmail || paymentData.customerEmail.trim() === '') {
      errors.push('Customer email is required');
    }

    if (!paymentData.tableNumber || paymentData.tableNumber.trim() === '') {
      errors.push('Table number is required');
    }

    return errors;
  }

  /**
   * Get payment method display name
   */
  getPaymentMethodDisplayName(method) {
    const methods = {
      razorpay: 'Online Payment',
      direct: 'Pay at Restaurant',
      card: 'Credit/Debit Card',
      upi: 'UPI',
      netbanking: 'Net Banking',
    };

    return methods[method] || method;
  }

  /**
   * Check if payment is completed
   */
  isPaymentCompleted(paymentStatus) {
    return ['completed', 'paid', 'success'].includes(paymentStatus?.toLowerCase());
  }

  /**
   * Get payment status color
   */
  getPaymentStatusColor(status) {
    const colors = {
      pending: 'text-yellow-600',
      completed: 'text-green-600',
      failed: 'text-red-600',
      cancelled: 'text-gray-600',
      refunded: 'text-blue-600',
    };

    return colors[status?.toLowerCase()] || 'text-gray-600';
  }

  /**
   * Retry mechanism for order creation
   * @private
   */
  async _createOrderWithRetry(orderData, maxRetries = 3) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const orderDataWithAttempt = { ...orderData, creationAttempt: attempt };
        const order = await this.createOrder(orderDataWithAttempt);
        if (order && order.id) {
          return order;
        } else {
          throw new Error('Order creation returned empty response');
        }
      } catch (error) {
        if (attempt === maxRetries) {
          throw new Error(`Order creation failed after ${maxRetries} attempts: ${error.message}`);
        }
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  /**
   * Notify backend and customer about payment-order mismatch
   */
  async notifyPaymentOrderMismatch(failedOrderData, restaurantName) {
    console.warn('PAYMENT MISMATCH ALERT:', {
      message: 'Payment succeeded but order creation failed',
      data: failedOrderData,
      restaurant: restaurantName,
    });

    try {
      const { error } = await supabase.functions.invoke('payment-alert', {
        body: {
          type: 'payment_success_order_failed',
          data: failedOrderData,
          urgency: 'high',
          customerEmail: failedOrderData.customerEmail,
          customerName: failedOrderData.customerName,
          restaurantName: restaurantName,
          paymentId: failedOrderData.paymentId,
          amount: failedOrderData.amount,
          tableNumber: failedOrderData.tableNumber,
        },
      });
      if (error) console.error('Alert function error:', error);
    } catch (e) {
      // safe fallback
    }
  }

  /**
   * Close recovery dialog
   * @private
   */
  _closeRecoveryDialog() {
    const dialog = document.querySelector('.fixed.inset-0');
    if (dialog) dialog.remove();
  }

  /**
   * Show recovery options to user
   * @private
   */
  _showPaymentRecoveryOptions(
    paymentId,
    { retryOrderCreation, contactSupport, closeRecoveryDialog },
  ) {
    const overlay = document.createElement('div');
    overlay.className =
      'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';

    const card = document.createElement('div');
    card.className = 'bg-white p-6 rounded-2xl max-w-md mx-4';

    const title = document.createElement('h3');
    title.className = 'text-lg font-bold mb-4 text-red-600';
    title.textContent = 'Payment Successful, Order Issue';

    const desc = document.createElement('p');
    desc.className = 'text-gray-700 mb-4';
    desc.textContent =
      "Your payment was processed successfully, but there was an issue creating your order. Don't worry — your money is safe!";

    const paymentIdBox = document.createElement('div');
    paymentIdBox.className = 'mb-4';
    const label = document.createElement('p');
    label.className = 'text-sm text-gray-600';
    label.textContent = 'Payment ID: ';
    const idSpan = document.createElement('span');
    idSpan.className = 'font-mono';
    idSpan.textContent = String(paymentId || '');
    label.appendChild(idSpan);
    paymentIdBox.appendChild(label);

    const btnContainer = document.createElement('div');
    btnContainer.className = 'space-y-3';

    const makeBtn = (text, cls, handler) => {
      const btn = document.createElement('button');
      btn.className = cls;
      btn.textContent = text;
      btn.onclick = handler;
      return btn;
    };

    btnContainer.appendChild(
      makeBtn(
        'Retry Order Creation',
        'w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600',
        retryOrderCreation,
      ),
    );
    btnContainer.appendChild(
      makeBtn(
        'Contact Support',
        'w-full bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600',
        contactSupport,
      ),
    );
    btnContainer.appendChild(
      makeBtn(
        'Close',
        'w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300',
        closeRecoveryDialog,
      ),
    );

    card.appendChild(title);
    card.appendChild(desc);
    card.appendChild(paymentIdBox);
    card.appendChild(btnContainer);
    overlay.appendChild(card);
    document.body.appendChild(overlay);
  }

  /**
   * Process order with payment integration
   */
  async processOrderWithPayment({
    paymentResponse,
    restaurant,
    user,
    cartItems,
    finalCustomerName,
    finalCustomerEmail,
    finalCustomerPhone,
    tableNumber,
    specialInstructions,
    paymentMethod,
    finalTotal,
    clearCart,
    navigate,
    setScriptError,
    setIsProcessing,
  }) {
    try {
      if (!paymentResponse || !paymentResponse.razorpay_payment_id) {
        throw new Error('Missing payment confirmation details');
      }
      if (!restaurant || !restaurant.id) {
        throw new Error('Restaurant data not available');
      }

      if (!user) {
        if (!finalCustomerName || finalCustomerName.trim() === '') {
          throw new Error('Customer name is required');
        }
        if (finalCustomerPhone && finalCustomerPhone.trim() !== '') {
          const phoneRegex = /^[6-9]\d{9}$/;
          if (!phoneRegex.test(finalCustomerPhone.trim())) {
            throw new Error('Please enter a valid 10-digit phone number or leave it blank');
          }
        }

        if (finalCustomerEmail && finalCustomerEmail.trim() !== '') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(finalCustomerEmail.trim())) {
            throw new Error('Please enter a valid email address');
          }
        }
      }

      const paymentData = {
        paymentId: paymentResponse.razorpay_payment_id,
        orderId: paymentResponse.razorpay_order_id,
        signature: paymentResponse.razorpay_signature,
        amount: finalTotal,
        currency: 'INR',
        status: 'captured',
        method: paymentMethod,
        timestamp: new Date().toISOString(),
        customerEmail: finalCustomerEmail.trim(),
        customerPhone: finalCustomerPhone.trim(),
        restaurantId: restaurant.id,
      };
      localStorage.setItem(
        `payment_${paymentResponse.razorpay_payment_id}`,
        JSON.stringify(paymentData),
      );

      const order = await this._createOrderWithRetry({
        restaurantId: restaurant.id,
        userId: user ? user.id : null,
        customerName: (finalCustomerName || 'Guest').trim(),
        customerEmail: resolveCustomerEmail(
          finalCustomerEmail,
          finalCustomerPhone || paymentResponse.razorpay_payment_id,
        ),
        customerPhone: (finalCustomerPhone || '').trim(),
        tableNumber,
        items: cartItems.map((item) => ({
          id: item.id,
          quantity: item.quantity,
          price: item.price,
          name: item.name,
          category: item.category,
          image: item.image,
        })),
        razorpayOrderId: paymentResponse.razorpay_order_id || null,
        razorpayPaymentId: paymentResponse.razorpay_payment_id || null,
        razorpaySignature: paymentResponse.razorpay_signature || null,
        paymentMethod:
          paymentMethod === 'razorpay' ? 'razorpay' : paymentMethod === 'upi' ? 'upi' : 'direct',
        paymentStatus: 'completed',
        specialInstructions,
        totalAmount: finalTotal,
      });

      localStorage.removeItem(`payment_${paymentResponse.razorpay_payment_id}`);

      clearCart();
      navigate('/order-confirmation-page', {
        state: {
          orderId: order.id,
          orderNumber: order.order_number,
          restaurantId: restaurant.id,
          restaurantName: restaurant.name,
          totalAmount: order.total,
          tableNumber,
          paymentMethod,
          customerName: finalCustomerName,
          customerEmail: finalCustomerEmail,
          customerPhone: finalCustomerPhone,
          specialInstructions,
          paymentId: paymentResponse.razorpay_payment_id,
        },
      });
      return true;
    } catch (error) {
      if (
        paymentResponse.razorpay_payment_id &&
        !paymentResponse.razorpay_payment_id.startsWith('DIRECT-')
      ) {
        await this._handlePaymentSuccessOrderFailure({
          paymentResponse,
          restaurant,
          cartItems,
          finalCustomerName,
          finalCustomerEmail,
          finalCustomerPhone,
          tableNumber,
          specialInstructions,
          paymentMethod,
          finalTotal,
          setScriptError,
          setIsProcessing,
          user,
          navigate,
          clearCart,
        });
      } else {
        setScriptError(`Order creation failed: ${error.message}`);
        setIsProcessing(false);
      }
      return false;
    }
  }

  /**
   * Handle edge case of payment success but order creation failure
   * @private
   */
  async _handlePaymentSuccessOrderFailure({
    paymentResponse,
    restaurant,
    cartItems,
    finalCustomerName,
    finalCustomerEmail,
    finalCustomerPhone,
    tableNumber,
    specialInstructions,
    paymentMethod,
    finalTotal,
    setScriptError,
    setIsProcessing,
    user,
    navigate,
    clearCart,
  }) {
    try {
      setScriptError(
        `IMPORTANT: Your payment was successful but there was an issue creating your order. ` +
          `Please don't worry - your money is safe. Our team has been notified and will contact you shortly. ` +
          `Payment ID: ${paymentResponse.razorpay_payment_id}`,
      );

      const failedOrderData = {
        paymentId: paymentResponse.razorpay_payment_id,
        orderId: paymentResponse.razorpay_order_id,
        signature: paymentResponse.razorpay_signature,
        amount: finalTotal,
        currency: 'INR',
        customerName: finalCustomerName,
        customerEmail: finalCustomerEmail,
        customerPhone: finalCustomerPhone,
        restaurantId: restaurant.id,
        tableNumber,
        items: cartItems,
        specialInstructions,
        paymentMethod,
        errorMessage: 'Order creation failed after payment',
        timestamp: new Date().toISOString(),
        status: 'payment_success_order_failed',
      };

      try {
        await supabase.from('failed_payment_orders').insert({
          razorpay_payment_id: failedOrderData.paymentId,
          razorpay_order_id: failedOrderData.orderId,
          razorpay_signature: failedOrderData.signature,
          restaurant_id: failedOrderData.restaurantId,
          user_id: user ? user.id : null,
          customer_name: failedOrderData.customerName,
          customer_email: failedOrderData.customerEmail,
          customer_phone: failedOrderData.customerPhone,
          table_number: failedOrderData.tableNumber,
          items: failedOrderData.items,
          amount: failedOrderData.amount,
          payment_method: failedOrderData.paymentMethod,
          special_instructions: failedOrderData.specialInstructions,
          error_message: failedOrderData.errorMessage,
          status: 'pending_recovery',
        });
      } catch (dbError) {
        console.error('Failed to persist failed order to DB:', dbError);
        sessionStorage.setItem(
          `failed_order_${failedOrderData.paymentId}`,
          JSON.stringify(failedOrderData),
        );
      }

      try {
        await this.notifyPaymentOrderMismatch(failedOrderData, restaurant.name);
      } catch (notifyError) {
        console.error('Notify failed:', notifyError);
      }

      this._showPaymentRecoveryOptions(paymentResponse.razorpay_payment_id, {
        retryOrderCreation: async () => {
          try {
            let recoveryData = failedOrderData;
            const { data: dbRecord } = await supabase
              .from('failed_payment_orders')
              .select('*')
              .eq('razorpay_payment_id', paymentResponse.razorpay_payment_id)
              .eq('status', 'pending_recovery')
              .single();

            if (dbRecord) {
              recoveryData = {
                paymentId: dbRecord.razorpay_payment_id,
                orderId: dbRecord.razorpay_order_id,
                signature: dbRecord.razorpay_signature,
                restaurantId: dbRecord.restaurant_id,
                customerName: dbRecord.customer_name,
                customerEmail: dbRecord.customer_email,
                customerPhone: dbRecord.customer_phone,
                tableNumber: dbRecord.table_number,
                items: dbRecord.items,
                amount: dbRecord.amount,
                paymentMethod: dbRecord.payment_method,
                specialInstructions: dbRecord.special_instructions,
              };
            }
            const order = await this._createOrderWithRetry({
              restaurantId: recoveryData.restaurantId,
              userId: user ? user.id : null,
              customerName: recoveryData.customerName,
              customerEmail: recoveryData.customerEmail,
              customerPhone: recoveryData.customerPhone,
              tableNumber: recoveryData.tableNumber,
              items: recoveryData.items,
              razorpayOrderId: recoveryData.orderId,
              razorpayPaymentId: recoveryData.paymentId,
              razorpaySignature: recoveryData.signature,
              paymentMethod: recoveryData.paymentMethod,
              paymentStatus: 'completed',
              specialInstructions: recoveryData.specialInstructions,
              totalAmount: recoveryData.amount,
              isRetry: true,
            });
            if (order) {
              await supabase
                .from('failed_payment_orders')
                .update({ status: 'recovered', recovered_order_id: order.id })
                .eq('razorpay_payment_id', paymentResponse.razorpay_payment_id);
              sessionStorage.removeItem(`failed_order_${paymentResponse.razorpay_payment_id}`);
              clearCart();
              navigate('/order-confirmation-page', {
                state: {
                  orderId: order.id,
                  orderNumber: order.order_number,
                  restaurantId: recoveryData.restaurantId,
                  restaurantName: restaurant.name,
                  totalAmount: order.total,
                  tableNumber: recoveryData.tableNumber,
                  paymentMethod: recoveryData.paymentMethod,
                  customerName: recoveryData.customerName,
                  customerEmail: recoveryData.customerEmail,
                  customerPhone: recoveryData.customerPhone,
                  specialInstructions: recoveryData.specialInstructions,
                  paymentId: paymentResponse.razorpay_payment_id,
                  wasRetry: true,
                },
              });
              this._closeRecoveryDialog();
            }
          } catch (error) {
            alert('Retry failed. Please contact customer support.');
          }
        },
        contactSupport: () => {
          const supportPhone = restaurant.phone || '1800-xxx-xxxx';
          const supportEmail = restaurant.email || 'support@restaurant.com';
          const message = `Payment ID: ${paymentResponse.razorpay_payment_id}\nAmount: ₹${finalTotal}\nRestaurant: ${restaurant.name}\nTable: ${tableNumber}\nIssue: Payment successful but order creation failed`;
          if (supportPhone) {
            window.open(
              `https://wa.me/${supportPhone}?text=${encodeURIComponent(message)}`,
              '_blank',
            );
          } else {
            window.open(
              `mailto:${supportEmail}?subject=Payment Issue - ${paymentResponse.razorpay_payment_id}&body=${encodeURIComponent(message)}`,
              '_blank',
            );
          }
        },
        closeRecoveryDialog: this._closeRecoveryDialog.bind(this),
      });
    } catch (error) {
      setScriptError(
        `Payment successful but order creation failed. Payment ID: ${paymentResponse.razorpay_payment_id}. ` +
          `Please contact customer support immediately.`,
      );
    }
    setIsProcessing(false);
  }

  /**
   * Check for pending failed orders on app load
   */
  async checkPendingFailedOrders(userId, showPaymentRecoveryOptions) {
    try {
      if (userId) {
        const { data: dbRecords } = await supabase
          .from('failed_payment_orders')
          .select('razorpay_payment_id')
          .eq('user_id', userId)
          .eq('status', 'pending_recovery')
          .order('created_at', { ascending: false })
          .limit(1);

        if (dbRecords && dbRecords.length > 0) {
          setTimeout(() => {
            showPaymentRecoveryOptions(dbRecords[0].razorpay_payment_id);
          }, 1000);
          return;
        }
      }

      const keys = Object.keys(sessionStorage);
      const failedOrderKeys = keys.filter((key) => key.startsWith('failed_order_'));
      if (failedOrderKeys.length > 0) {
        const paymentId = failedOrderKeys[0].replace('failed_order_', '');
        setTimeout(() => {
          showPaymentRecoveryOptions(paymentId);
        }, 1000);
      }
    } catch (err) {
      console.error('checkPendingFailedOrders error:', err);
    }
  }
}

// Instantiate singleton
export const paymentService = new PaymentService();

// Backwards-compatible bindings
export const loadRazorpayScript = paymentService.loadRazorpayScript.bind(paymentService);
export const getRestaurantPaymentImage =
  paymentService.getRestaurantPaymentImage.bind(paymentService);
export const initializeRazorpayPayment =
  paymentService.initializeRazorpayPayment.bind(paymentService);
export const createRazorpayOrder = paymentService.createRazorpayOrder.bind(paymentService);
export const verifyRazorpaySignature = paymentService.verifyRazorpaySignature.bind(paymentService);
export const calculateOrderTotals = paymentService.calculateOrderTotals.bind(paymentService);
export const createOrder = paymentService.createOrder.bind(paymentService);
export const completeLiveOrderPayment =
  paymentService.completeLiveOrderPayment.bind(paymentService);
export const updatePaymentStatus = paymentService.updatePaymentStatus.bind(paymentService);
export const getOrderById = paymentService.getOrderById.bind(paymentService);
export const getUserOrders = paymentService.getUserOrders.bind(paymentService);
export const getRestaurantOrders = paymentService.getRestaurantOrders.bind(paymentService);
export const processRazorpayPayment = paymentService.processRazorpayPayment.bind(paymentService);
export const processDirectPayment = paymentService.processDirectPayment.bind(paymentService);
export const completePayment = paymentService.completePayment.bind(paymentService);
export const formatCurrency = paymentService.formatCurrency.bind(paymentService);
export const validatePaymentData = paymentService.validatePaymentData.bind(paymentService);
export const getPaymentMethodDisplayName =
  paymentService.getPaymentMethodDisplayName.bind(paymentService);
export const isPaymentCompleted = paymentService.isPaymentCompleted.bind(paymentService);
export const getPaymentStatusColor = paymentService.getPaymentStatusColor.bind(paymentService);
export const notifyPaymentOrderMismatch =
  paymentService.notifyPaymentOrderMismatch.bind(paymentService);
export const processOrderWithPayment = paymentService.processOrderWithPayment.bind(paymentService);
export const checkPendingFailedOrders =
  paymentService.checkPendingFailedOrders.bind(paymentService);

export default {
  loadRazorpayScript,
  initializeRazorpayPayment,
  verifyRazorpaySignature,
  processRazorpayPayment,
  calculateOrderTotals,
  createOrder,
  updatePaymentStatus,
  getOrderById,
  getUserOrders,
  getRestaurantOrders,
  processDirectPayment,
  completePayment,
  formatCurrency,
  validatePaymentData,
  getPaymentMethodDisplayName,
  isPaymentCompleted,
  getPaymentStatusColor,
  processOrderWithPayment,
  checkPendingFailedOrders,
};
